import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  FileArchive,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileAudio,
  Download,
  Loader2,
  X,
  Maximize2,
  Copy,
} from 'lucide-react';
import type { Message } from '../../api/chat';
import { getAttachmentDownloadUrl } from '../../api/chat';
import { fmtFileSize } from '../../utils/chat/format';

// Derive the backend origin from REACT_APP_CHAT_API_URL by stripping the /chat-api path suffix.
// This ensures relative paths like /files/chat/<name> resolve to the correct backend server.
const _chatApiUrl = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:8001/chat-api';
const API_BASE = _chatApiUrl.replace(/\/chat-api\/?$/, '');

export function resolveAttachmentUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/.test(url)) return url;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return `${API_BASE}/${url}`;
}

function fileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toUpperCase() : '';
}

function iconForMime(mime: string | null | undefined) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return FileImage;
  if (m.startsWith('video/')) return FileVideo;
  if (m.startsWith('audio/')) return FileAudio;
  if (m.includes('zip') || m.includes('compressed') || m.includes('archive')) return FileArchive;
  if (m.includes('sheet') || m.includes('excel') || m === 'text/csv') return FileSpreadsheet;
  return FileText;
}

function isPdfMime(mime: string | null | undefined, fileName: string): boolean {
  const m = (mime || '').toLowerCase();
  return m === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
}

function isSpreadsheetMime(mime: string | null | undefined, fileName: string): boolean {
  const m = (mime || '').toLowerCase();
  const n = fileName.toLowerCase();
  return (
    m.includes('sheet') ||
    m.includes('excel') ||
    m === 'text/csv' ||
    n.endsWith('.xls') ||
    n.endsWith('.xlsx') ||
    n.endsWith('.csv')
  );
}

function typeLabelFor(mime: string | null | undefined, fileName: string): string {
  const m = (mime || '').toLowerCase();
  const ext = fileExtension(fileName).toLowerCase();
  if (isPdfMime(mime, fileName)) return 'pdf';
  if (isSpreadsheetMime(mime, fileName)) {
    if (ext === 'csv') return 'csv';
    return 'xlsx';
  }
  if (m.includes('word') || ext === 'doc' || ext === 'docx') return 'docx';
  if (m.includes('presentation') || ext === 'ppt' || ext === 'pptx') return 'pptx';
  if (ext) return ext;
  return 'file';
}


// ── Lightbox ───────────────────────────────────────────────────────────
export function ImageLightbox({
  src,
  fileName,
  onClose,
}: {
  src: string;
  fileName: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter') { e.preventDefault(); forceDownload(src, fileName); }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, src, fileName]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={fileName}
      onClick={onClose}
      className="gc-lightbox-backdrop fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        background: 'rgba(8, 14, 11, 0.78)',
        backdropFilter: 'blur(12px) saturate(120%)',
        WebkitBackdropFilter: 'blur(12px) saturate(120%)',
        animation: 'gcLightboxFade 180ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 left-0 right-0 flex items-center gap-3 px-5 py-4"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0))',
        }}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-white/95 truncate">{fileName}</p>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); copyFileToClipboard(src, fileName); }}
          title="Copy"
          aria-label="Copy"
          className="grid place-items-center h-9 w-9 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition"
        >
          <Copy size={15} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); forceDownload(src, fileName); }}
          title="Download"
          aria-label="Download"
          className="grid place-items-center h-9 w-9 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition"
        >
          <Download size={16} />
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close preview"
          className="grid place-items-center h-9 w-9 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition"
        >
          <X size={18} />
        </button>
      </div>

      <img
        src={src}
        alt={fileName}
        onClick={(e) => e.stopPropagation()}
        className="rounded-lg shadow-2xl"
        style={{
          maxWidth: 'min(92vw, 1400px)',
          maxHeight: '86vh',
          objectFit: 'contain',
          animation: 'gcLightboxZoom 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />

      <style>{`
        @keyframes gcLightboxFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes gcLightboxZoom {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>,
    document.body,
  );
}

// ── Voice player ──────────────────────────────────────────────────────
function fmtClock(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function VoiceAudio({ src, isMine }: { src: string; isMine: boolean }) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [rate, setRate] = React.useState<1 | 1.5 | 2>(1);

  React.useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => {
      // Some browsers report Infinity for webm/opus blobs; guard against NaN.
      const d = a.duration;
      setDuration(Number.isFinite(d) ? d : 0);
      setLoaded(true);
    };
    const onTime = () => setCurrentTime(a.currentTime);
    const onEnd = () => { setPlaying(false); setCurrentTime(0); };
    const onErr = () => setError(true);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('durationchange', onLoaded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('durationchange', onLoaded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onErr);
    };
  }, []);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (a.paused) {
        await a.play();
        setPlaying(true);
      } else {
        a.pause();
        setPlaying(false);
      }
    } catch {
      setError(true);
    }
  };

  const seekFromEvent = (clientX: number) => {
    const el = trackRef.current;
    const a = audioRef.current;
    if (!el || !a || !duration) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setCurrentTime(a.currentTime);
  };

  const cycleRate = () => {
    const next: 1 | 1.5 | 2 = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  if (error) {
    // A plain <a download> is ignored cross-origin and would navigate (in the
    // desktop app: to the system browser); route it like every other download.
    return (
      <button
        type="button"
        onClick={() => forceDownload(src, src.split('/').pop()?.split('?')[0] || 'voice-note')}
        className={`gc-link-sm ${isMine ? 'text-white' : ''}`}
      >
        Download audio
      </button>
    );
  }

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const fg = isMine ? '#ffffff' : 'var(--gc-accent-strong, #047857)';
  const trackBg = isMine ? 'rgba(255,255,255,0.25)' : 'rgba(15,29,20,0.12)';
  const labelColor = isMine ? 'rgba(255,255,255,0.85)' : 'var(--gc-text-secondary, #374151)';

  return (
    <div className="flex items-center gap-2.5 flex-1 min-w-0">
      <audio ref={audioRef} preload="metadata" src={src} className="hidden" />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause voice message' : 'Play voice message'}
        className="grid place-items-center flex-shrink-0 rounded-full transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
        style={{
          width: 32,
          height: 32,
          background: isMine ? 'rgba(255,255,255,0.92)' : 'var(--gc-accent-strong, #047857)',
          color: isMine ? 'var(--gc-bubble-mine-bg, #0f1d14)' : '#ffffff',
        }}
      >
        {playing ? (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
            <path d="M8 5.14v13.72c0 .79.87 1.27 1.54.84l10.79-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
          </svg>
        )}
      </button>

      <div
        ref={trackRef}
        onClick={(e) => seekFromEvent(e.clientX)}
        role="slider"
        aria-label="Seek voice message"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(currentTime)}
        tabIndex={0}
        className="relative flex-1 min-w-0 cursor-pointer"
        style={{ height: 18 }}
      >
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-full"
          style={{ height: 3, background: trackBg }}
        />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full transition-[width] duration-100"
          style={{ height: 3, width: `${pct}%`, background: fg }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full shadow-sm transition-[left] duration-100"
          style={{
            left: `${pct}%`,
            width: 10,
            height: 10,
            background: fg,
            opacity: loaded ? 1 : 0.4,
          }}
        />
      </div>

      <span
        className="font-mono tabular-nums flex-shrink-0 select-none"
        style={{ fontSize: 11, color: labelColor, minWidth: 32, textAlign: 'right' }}
      >
        {fmtClock(playing || currentTime > 0 ? currentTime : duration)}
      </span>

      <button
        type="button"
        onClick={cycleRate}
        aria-label={`Playback speed ${rate}x`}
        title={`Speed ${rate}×`}
        className="flex-shrink-0 px-1.5 rounded-md font-semibold focus:outline-none transition-colors"
        style={{
          fontSize: 10,
          height: 18,
          color: labelColor,
          background: isMine ? 'rgba(255,255,255,0.15)' : 'rgba(15,29,20,0.06)',
          minWidth: 28,
        }}
      >
        {rate}×
      </button>
    </div>
  );
}

// ── File card (for non-image attachments + image error fallback) ──────
function FileCard({
  url,
  fileName,
  fileSize,
  mime,
  isMine,
}: {
  url: string;
  fileName: string;
  fileSize: number;
  mime: string | null | undefined;
  isMine: boolean;
}) {
  const Icon = iconForMime(mime);
  const ext = fileExtension(fileName);

  // Self-contained colors that read on any bubble background.
  // Mine bubbles are dark sage green — render a slightly lighter inset.
  // Theirs bubbles are surface-0 — render a tinted surface-2 inset.
  const cardBg = isMine ? 'rgba(255,255,255,0.10)' : 'var(--gc-surface-2)';
  const cardBgHover = isMine ? 'rgba(255,255,255,0.16)' : 'var(--gc-surface-3)';
  const cardBorder = isMine ? 'rgba(255,255,255,0.16)' : 'rgba(15,29,20,0.08)';
  const iconTileBg = isMine ? 'rgba(255,255,255,0.92)' : 'var(--gc-accent-strong)';
  const iconColor = isMine ? 'var(--gc-bubble-mine-bg)' : '#ffffff';
  const titleColor = isMine ? '#ffffff' : 'var(--gc-text-primary)';
  const metaColor = isMine ? 'rgba(255,255,255,0.70)' : 'var(--gc-text-tertiary)';
  const dlColor = isMine ? 'rgba(255,255,255,0.85)' : 'var(--gc-text-secondary)';

  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await copyFileToClipboard(url, fileName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      onClick={() => setPreviewOpen(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewOpen(true); } }}
      aria-label={`Preview ${fileName}`}
      className="gc-att-card group relative flex items-center gap-3 rounded-2xl transition-all duration-200 text-left"
      style={{
        background: cardBg,
        border: 'none',
        width: '260px',
        maxWidth: '100%',
        padding: '12px 14px 12px 12px',
        cursor: 'pointer',
        ['--gc-att-bg-hover' as any]: cardBgHover,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = cardBgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = cardBg)}
    >
      <div
        className="relative grid place-items-center rounded-xl flex-shrink-0 transition-transform duration-200 group-hover:-rotate-2 group-hover:scale-[1.04]"
        style={{
          height: 44,
          width: 44,
          background: iconTileBg,
          color: iconColor,
          boxShadow: isMine ? 'none' : '0 1px 2px rgba(15,29,20,0.10)',
        }}
      >
        <Icon size={20} strokeWidth={1.75} />
        {ext && (
          <span
            className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-md leading-none"
            style={{
              fontSize: '8.5px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              background: isMine ? 'var(--gc-bubble-mine-bg)' : 'var(--gc-accent-strong)',
              color: '#ffffff',
              border: `1.5px solid ${isMine ? 'rgba(255,255,255,0.95)' : '#ffffff'}`,
            }}
          >
            {ext.length > 4 ? ext.slice(0, 4) : ext}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p
          className="font-medium leading-tight truncate"
          style={{ fontSize: '13px', color: titleColor }}
        >
          {fileName}
        </p>
        <p
          className="mt-1 flex items-center gap-1.5"
          style={{ fontSize: '11px', color: metaColor }}
        >
          <span>{fmtFileSize(fileSize)}</span>
          <span aria-hidden style={{ opacity: 0.55 }}>·</span>
          <span style={{ opacity: 0.85 }}>Click to preview</span>
        </p>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy ${fileName}`}
          title={copied ? 'Copied' : 'Copy'}
          className="grid place-items-center rounded-full transition-all duration-200"
          style={{ color: dlColor, width: 26, height: 26, opacity: copied ? 1 : 0.6 }}
        >
          {copied ? <span style={{ fontSize: 10, fontWeight: 600 }}>OK</span> : <Copy size={13} />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); forceDownload(url, fileName); }}
          aria-label={`Download ${fileName}`}
          title="Download"
          className="flex-shrink-0 grid place-items-center rounded-full transition-all duration-200 opacity-60 hover:opacity-100"
          style={{ color: dlColor, width: 26, height: 26 }}
        >
          <Download size={15} />
        </button>
      </div>
    </div>
    {previewOpen && (
      <DocPreviewModal
        url={url}
        fileName={fileName}
        onClose={() => setPreviewOpen(false)}
      />
    )}
    </>
  );
}

// ── Document card with top preview (PDF first-page, Excel tile) ─────
const DOC_TILE: Record<string, { gradient: string; accent: string; iconColor: string }> = {
  pdf:  { gradient: 'linear-gradient(135deg,#fee2e2 0%,#fecaca 100%)', accent: '#dc2626', iconColor: '#b91c1c' },
  xlsx: { gradient: 'linear-gradient(135deg,#dcfce7 0%,#bbf7d0 100%)', accent: '#16a34a', iconColor: '#15803d' },
  csv:  { gradient: 'linear-gradient(135deg,#dcfce7 0%,#bbf7d0 100%)', accent: '#16a34a', iconColor: '#15803d' },
  docx: { gradient: 'linear-gradient(135deg,#dbeafe 0%,#bfdbfe 100%)', accent: '#2563eb', iconColor: '#1d4ed8' },
  pptx: { gradient: 'linear-gradient(135deg,#ffedd5 0%,#fed7aa 100%)', accent: '#ea580c', iconColor: '#c2410c' },
  file: { gradient: 'linear-gradient(135deg,#e5e7eb 0%,#d1d5db 100%)', accent: '#4b5563', iconColor: '#374151' },
};

// Force-download an attachment regardless of the `download` attr being
// ignored for cross-origin URLs. Tries fetch+blob first (works for
// same-origin or CORS-enabled URLs) and falls back to opening in a new
// tab when CORS blocks the fetch.
export async function forceDownload(url: string, fileName: string): Promise<void> {
  // Electron: let the main process download it — no CORS there (the GCS
  // bucket sends none, so the fetch below fails and the fallback would open
  // the system browser) and the OS "Save as" dialog opens over the app.
  const api = (window as any).electronAPI;
  if (api?.downloadFile && await api.downloadFile(url, fileName)) return;

  // Web: the bucket sends no CORS headers, so the fetch below fails for GCS
  // attachments and its new-tab fallback is where the browser showed its
  // Save As. Instead ask the API for a signed URL that forces
  // `Content-Disposition: attachment` and load it in a hidden iframe: the
  // browser turns that into a download in THIS tab, so Save As opens over
  // the messenger. (Signed URLs — DBMS links — are not chat attachments.)
  if (/^https:\/\/storage\.googleapis\.com\//.test(url) && !/[?&]X-Goog-Signature=/.test(url)) {
    try {
      const signed = await getAttachmentDownloadUrl(url);
      if (signed) {
        const frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.src = signed;
        document.body.appendChild(frame);
        setTimeout(() => frame.remove(), 60_000);
        return;
      }
    } catch { /* not a chat attachment or no access — try the generic paths */ }
  }

  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
  } catch {
    // Fallback when CORS blocks the fetch — open in a new tab so the
    // user can right-click → Save As. Better than nothing.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// Copy a file to the system clipboard. In Electron it uses the main-process
// clipboard API (an image goes on as a picture, anything else as a real file
// Explorer / Finder can paste). On the web it tries the ClipboardItem API,
// which works for images, and otherwise falls back to the file's LINK.
//
// The fallback used to write the file NAME, which is why copying a picture in
// the browser and pasting it anywhere produced the words "image-1762...png".
// A name is not the thing anybody was copying; a link at least opens the file.
export async function copyFileToClipboard(url: string, fileName: string): Promise<void> {
  const isImage = /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(fileName);

  // Electron: delegate to the main process (writes to OS native clipboard)
  const api = (window as any).electronAPI;
  if (api?.copyFile) {
    await api.copyFile(url, fileName);
    return;
  }

  // Web: try the ClipboardItem API (works reliably for images)
  try {
    if (isImage) {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const png = blob.type === 'image/png' ? blob : await convertToPng(blob);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      return;
    }
    // Non-image: try generic ClipboardItem (limited browser support)
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
  } catch {
    // GCS serves attachments without CORS headers, so the fetch above fails
    // for every file in a plain browser tab — this is the normal path there,
    // not an edge case.
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* silently fail */ }
  }
}

// Convert any image blob to PNG so ClipboardItem works reliably.
async function convertToPng(blob: Blob): Promise<Blob> {
  const img = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  img.close();
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
}

// Google Docs Viewer URL — Google's server fetches the file, so no bucket CORS
// is required. Only works for publicly accessible URLs.
function gviewUrl(fileUrl: string): string {
  return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`;
}

// Fullscreen lightbox-style preview for documents. Renders the file inside a
// Google Docs Viewer iframe so PDF / XLS / DOC / PPT all preview the same way.
function DocPreviewModal({
  url,
  fileName,
  onClose,
}: {
  url: string;
  fileName: string;
  onClose: () => void;
}) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter') { e.preventDefault(); forceDownload(url, fileName); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, url, fileName]);

  const isPublicHttp = /^https?:/i.test(url) && !url.includes('/files/chat/');
  const viewerSrc = isPublicHttp ? gviewUrl(url) : null;

  return createPortal(
    <div
      className="gc-doc-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${fileName}`}
    >
      <div className="gc-doc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gc-doc-modal__bar">
          <span className="gc-doc-modal__title" title={fileName}>{fileName}</span>
          <div className="gc-doc-modal__actions">
            <button
              type="button"
              onClick={() => copyFileToClipboard(url, fileName)}
              className="gc-doc-modal__btn"
              title="Copy"
              aria-label="Copy"
            >
              <Copy size={15} />
            </button>
            <button
              type="button"
              onClick={() => forceDownload(url, fileName)}
              className="gc-doc-modal__btn"
              title="Download"
              aria-label="Download"
            >
              <Download size={16} />
            </button>
            <button
              type="button"
              className="gc-doc-modal__btn"
              onClick={onClose}
              title="Close"
              aria-label="Close preview"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="gc-doc-modal__body">
          {!viewerSrc && (
            <div className="gc-doc-modal__empty">
              <p>This file can't be previewed in the browser — try downloading it.</p>
              <button
                type="button"
                onClick={() => forceDownload(url, fileName)}
                className="gc-doc-modal__primary"
              >
                <Download size={14} /> Download {fileName}
              </button>
            </div>
          )}
          {viewerSrc && !loaded && (
            <div className="gc-doc-modal__loader">
              <Loader2 size={28} className="animate-spin" />
              <span>Loading preview…</span>
            </div>
          )}
          {viewerSrc && (
            <iframe
              src={viewerSrc}
              title={fileName}
              className="gc-doc-modal__iframe"
              onLoad={() => setLoaded(true)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DocumentCard({
  url,
  fileName,
  fileSize,
  mime,
  isMine,
}: {
  url: string;
  fileName: string;
  fileSize: number;
  mime: string | null | undefined;
  isMine: boolean;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const typeLabel = typeLabelFor(mime, fileName);
  const tile = DOC_TILE[typeLabel] || DOC_TILE.file;
  const Icon = iconForMime(mime);

  const cardBg = isMine ? 'rgba(255,255,255,0.10)' : 'var(--gc-surface-2)';
  const cardBgHover = isMine ? 'rgba(255,255,255,0.16)' : 'var(--gc-surface-3)';
  const cardBorder = isMine ? 'rgba(255,255,255,0.16)' : 'rgba(15,29,20,0.08)';
  const titleColor = isMine ? '#ffffff' : 'var(--gc-text-primary)';
  const metaColor = isMine ? 'rgba(255,255,255,0.70)' : 'var(--gc-text-tertiary)';
  const dlColor = isMine ? 'rgba(255,255,255,0.85)' : 'var(--gc-text-secondary)';

  const metaParts: string[] = [fmtFileSize(fileSize), typeLabel];

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      onClick={() => setPreviewOpen(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewOpen(true); } }}
      aria-label={`Preview ${fileName}`}
      className="gc-att-doc group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-200 text-left"
      style={{
        background: cardBg,
        border: 'none',
        width: '280px',
        maxWidth: '100%',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = cardBgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = cardBg)}
    >
      <div
        className="relative w-full grid place-items-center overflow-hidden"
        style={{
          height: 170,
          background: tile.gradient,
        }}
      >
        <div
          className="grid place-items-center rounded-2xl transition-transform duration-200 group-hover:scale-[1.04]"
          style={{
            height: 80,
            width: 80,
            background: 'rgba(255,255,255,0.85)',
            boxShadow: '0 4px 12px rgba(15,29,20,0.10)',
          }}
        >
          <Icon size={40} strokeWidth={1.5} style={{ color: tile.iconColor }} />
        </div>
        <span
          className="absolute top-2 right-2 px-2 py-0.5 rounded-md uppercase"
          style={{
            fontSize: '9.5px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            background: tile.accent,
            color: '#ffffff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            zIndex: 2,
          }}
        >
          {typeLabel}
        </span>
        {/* Hover overlay — appears on the preview tile while hovering the card. */}
        <div className="gc-att-doc__hover" aria-hidden>
          <Maximize2 size={18} />
          <span>Click to preview</span>
        </div>
      </div>

      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className="grid place-items-center rounded-lg flex-shrink-0"
          style={{
            height: 36,
            width: 36,
            background: isMine ? 'rgba(255,255,255,0.92)' : tile.accent,
            color: isMine ? tile.iconColor : '#ffffff',
          }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="font-medium leading-tight truncate"
            style={{ fontSize: '13px', color: titleColor }}
            title={fileName}
          >
            {fileName}
          </p>
          <p
            className="mt-0.5 truncate"
            style={{ fontSize: '11px', color: metaColor }}
          >
            {metaParts.join(' • ')}
          </p>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); forceDownload(url, fileName); }}
          aria-label={`Download ${fileName}`}
          title="Download"
          className="flex-shrink-0 grid place-items-center rounded-full transition-all duration-200 opacity-60 hover:opacity-100"
          style={{ color: dlColor, width: 26, height: 26 }}
        >
          <Download size={15} />
        </button>
      </div>
    </div>
    {previewOpen && (
      <DocPreviewModal
        url={url}
        fileName={fileName}
        onClose={() => setPreviewOpen(false)}
      />
    )}
    </>
  );
}

// ── Image attachment with lightbox + graceful fallback ────────────────
function ImageAttachment({
  url,
  fileName,
  fileSize,
  mime,
  isMine,
  bleed = false,
}: {
  url: string;
  fileName: string;
  fileSize: number;
  mime: string | null | undefined;
  isMine: boolean;
  // When true the image fills its bubble edge-to-edge (square corners, full
  // width); the parent bubble supplies the rounding via overflow-hidden.
  bleed?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);

  if (errored) {
    return <FileCard url={url} fileName={fileName} fileSize={fileSize} mime={mime} isMine={isMine} />;
  }

  return (
    <>
      <div
        className={`group relative overflow-hidden ${bleed ? 'rounded-none' : 'rounded-2xl'}`}
        style={{
          width: bleed ? '100%' : 260,
          maxWidth: '100%',
          aspectRatio: '4 / 3',
          background: isMine ? 'rgba(15,29,20,0.45)' : 'var(--gc-surface-2)',
          boxShadow: isMine
            ? 'inset 0 0 0 1px rgba(255,255,255,0.10)'
            : 'inset 0 0 0 1px rgba(15,29,20,0.06)',
        }}
      >
        {!loaded && (
          <div
            aria-hidden
            className="absolute inset-0 animate-pulse"
            style={{
              background: isMine
                ? 'linear-gradient(110deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.04) 75%)'
                : 'linear-gradient(110deg, rgba(31,46,35,0.04) 25%, rgba(31,46,35,0.08) 50%, rgba(31,46,35,0.04) 75%)',
              backgroundSize: '200% 100%',
              animation: 'gcShimmer 1.4s linear infinite',
            }}
          />
        )}

        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Open preview of ${fileName}`}
          className="absolute inset-0 w-full h-full cursor-zoom-in"
        >
          <img
            src={url}
            alt={fileName}
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={[
              'w-full h-full object-cover transition-all duration-500',
              loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.02]',
              'group-hover:scale-[1.03]',
            ].join(' ')}
            draggable={false}
          />
        </button>

        {/* Bottom gradient with filename + size */}
        <div
          className={[
            'absolute inset-x-0 bottom-0 px-3 pt-8 pb-2.5 pointer-events-none',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-200',
          ].join(' ')}
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 10%, rgba(0,0,0,0))',
          }}
        >
          <p className="text-[12px] font-medium text-white truncate drop-shadow">
            {fileName}
          </p>
          <p className="text-[10.5px] text-white/75 mt-0.5">{fmtFileSize(fileSize)}</p>
        </div>

        {/* Top-right action chips */}
        <div
          className={[
            'absolute top-2 right-2 flex items-center gap-1.5',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-200',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyFileToClipboard(url, fileName); }}
            title="Copy"
            aria-label="Copy"
            className="grid place-items-center h-7 w-7 rounded-full bg-black/45 backdrop-blur-md text-white hover:bg-black/65 transition"
          >
            <Copy size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); forceDownload(url, fileName); }}
            title="Download"
            aria-label="Download"
            className="grid place-items-center h-7 w-7 rounded-full bg-black/45 backdrop-blur-md text-white hover:bg-black/65 transition"
          >
            <Download size={13} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Open preview"
            className="grid place-items-center h-7 w-7 rounded-full bg-black/45 backdrop-blur-md text-white hover:bg-black/65 transition"
          >
            <Maximize2 size={13} />
          </button>
        </div>

        <style>{`
          @keyframes gcShimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>

      {open && <ImageLightbox src={url} fileName={fileName} onClose={() => setOpen(false)} />}
    </>
  );
}

interface Props {
  att: NonNullable<Message['attachments']>[0];
  isMine: boolean;
  msg?: Message;
  // Render images edge-to-edge inside a padding-less bubble (set by MessageBubble
  // when the image has no quote/forward header above it).
  bleed?: boolean;
}

function AttachmentBubble({ att, isMine, msg, bleed = false }: Props) {
  const url = resolveAttachmentUrl(att.file_url);
  const isAudio =
    msg?.message_type === 'voice' ||
    (att.mime_type || '').startsWith('audio/');
  const isVoice = isAudio;

  // ── Voice message ──────────────────────────────────────────────────
  if (isVoice) {
    return (
      <div className="w-[280px] max-w-full">
        <div
          className={`flex items-center gap-2 px-2.5 py-2 rounded-xl ${
            isMine ? 'bg-white/10' : ''
          }`}
        >
          <VoiceAudio src={resolveAttachmentUrl(att.file_url)} isMine={isMine} />
        </div>
      </div>
    );
  }

  if (att.mime_type?.startsWith('image/')) {
    return (
      <ImageAttachment
        url={url}
        fileName={att.file_name}
        fileSize={att.file_size}
        mime={att.mime_type}
        isMine={isMine}
        bleed={bleed}
      />
    );
  }

  // PDFs, spreadsheets, Office docs → WhatsApp-style card with preview
  const fname = att.file_name || '';
  const useDocCard =
    isPdfMime(att.mime_type, fname) ||
    isSpreadsheetMime(att.mime_type, fname) ||
    /\.(docx?|pptx?)$/i.test(fname) ||
    (att.mime_type || '').includes('word') ||
    (att.mime_type || '').includes('presentation');

  if (useDocCard) {
    return (
      <DocumentCard
        url={url}
        fileName={att.file_name}
        fileSize={att.file_size}
        mime={att.mime_type}
        isMine={isMine}
      />
    );
  }

  return (
    <FileCard
      url={url}
      fileName={att.file_name}
      fileSize={att.file_size}
      mime={att.mime_type}
      isMine={isMine}
    />
  );
}

export default AttachmentBubble;
